<template>
  <div>
      <multiselect
        v-model="internalValue"
        :label="label"
        :track-by="trackBy"
        :placeholder="$t('pmd.uiComponents.typeToSearch')"
        open-direction="bottom"
        class="hl-contact-serch"
        :preserve-search="true"
        :options="options"
        :searchable="true"
        :loading="isLoading"
        :limit="3"
        :max-height="600"
        :show-no-results="false"
        :hide-selected="true"
        @update:model-value="handleInput"
        @search-change="asyncFind">
          <template #singleLabel="props" >
            <Avatar
              :contact="props.option"
              :include_name="true"
              :include_company_name="true"
            />
            <span class="option__desc">
              <span class="option__title">{{ props.option.label }}</span>
            </span>
          </template>
          <template #option="props">
            <Avatar
              :contact="props.option"
              :include_name="true"
              :include_company_name="true"
            />
            <div class="option__desc">
              <span class="option__title">{{ props.option.label }}</span>
            </div>
          </template>
      </multiselect>
  </div>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, ref, watch } from 'vue'

const Avatar = defineAsyncComponent(() => import('@/pmd/components/Avatar.vue'))

import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import axios from 'axios'

// Define contact type interface
interface Contact {
  value: string;
  label: string;
  name: string;
  prettyName: string;
  initials: string;
  profileColor: string;
  [key: string]: any;
}

export default defineComponent({
  components: {
    Multiselect,
    Avatar
  },
  props: {
    modelValue: {
      type: [Object, Array] as any,
      default: null
    },
    label: {
      type: String,
      default: 'label'
    },
    trackBy: {
      type: String,
      default: 'value'
    },
    searchUrl: {
      type: String,
      default: ''
    },
    locationId: {
      type: String,
      default: ''
    },
    isClear: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const internalValue = ref<Contact | Contact[] | null>(props.modelValue as Contact | Contact[] | null);
    const options = ref<Contact[]>([]);
    const isLoading = ref(false);

    // Watch for changes from parent
    watch(() => props.modelValue, (newValue) => {
      // Only update internalValue if it's actually different
      if (JSON.stringify(internalValue.value) !== JSON.stringify(newValue)) {
        internalValue.value = newValue as Contact | Contact[] | null;
      }
    });

    // Watch for internal value changes
    watch(internalValue, (newValue) => {
      // Only emit if the value has actually changed from the props
      if (JSON.stringify(props.modelValue) !== JSON.stringify(newValue)) {
        emit('update:modelValue', newValue);
      }
    });

    // Watch for clear signal
    watch(() => props.isClear, () => {
      clearAll();
    });

    function handleInput(value: Contact | Contact[] | null) {
      emit('change', value);
    }

    function limitText(count: number) {
      return `and ${count} other countries`;
    }

    function initials(name: string) {
      if (!name) {
        return '';
      }

      const splitStr = name.toLowerCase().split(' ');
      for (let i = 0; i < splitStr.length; i++) {
        // You do not need to check if i is larger than splitStr length, as your for does that for you
        // Assign it back to the array
        splitStr[i] = splitStr[i].charAt(0).toUpperCase();
      }
      // Directly return the joined string
      return splitStr.join('');
    }

    function asyncFind(query: string) {
      isLoading.value = true;
      axios
        .get(props.searchUrl, {
          params: {
            location_id: props.locationId,
            q: query,
            page: 1,
            limit: 10,
          },
        })
        .then(res => {
          options.value = res.data.hits.hits.map((contact: any) => {
            return {
              value: contact._id,
              label: contact._source.contact_name,
              name: contact._id,
              prettyName: contact._source.contact_name,
              initials: initials(contact._source.contact_name),
              profileColor: 'rgb(134, 117, 189)',
            };
          });

          isLoading.value = false;
        })
        .catch(error => {
          console.error('Error fetching contacts:', error);
          isLoading.value = false;
        });
    }

    function clearAll() {
      internalValue.value = null;
      emit('update:modelValue', null);
    }

    return {
      internalValue,
      options,
      isLoading,
      limitText,
      initials,
      asyncFind,
      clearAll,
      handleInput
    };
  }
});
</script>

<style scoped>
.hl-contact-serch .multiselect__option {
  display: flex;
  align-items: center;
}

.hl-contact-serch .multiselect__tags {
  padding-top: 5px;
}

.hl-contact-serch .multiselect__single {
  margin-bottom: 4px;
}
.hl-contact-serch .multiselect__single .avatar {
  height: 32px;
}
</style>
